/**
 * Shared design tokens — dark portfolio shell with a clear blue accent.
 * Keep CSS variables in src/index.css in sync with these values.
 *
 * Domain brands (Spotify green, Twitter blue) stay local to those features.
 */
export const tokens = {
  colors: {
    bg: '#000000',
    bgElevated: '#121212',
    surface: 'rgba(17, 17, 17, 0.85)',
    border: 'rgba(255, 255, 255, 0.06)',
    borderStrong: 'rgba(255, 255, 255, 0.12)',
    text: '#ffffff',
    textMuted: '#cccccc',
    textDim: 'rgba(255, 255, 255, 0.55)',
    /** Brand primary — standard blue (home signal). */
    accent: '#3B82F6',
    accentHover: '#2563EB',
    /** Softer companion for secondary fills / highlights. */
    accent2: '#60A5FA',
    /** RGB channels for rgba() soft fills — matches accent. */
    accentRgb: '59, 130, 246',
  },
  gradients: {
    /** Page wash only — same-family neutrals, not a brand rainbow. */
    page: 'linear-gradient(180deg, #000000 0%, #0c0c0c 100%)',
    /** Optional mono-blue wash (avatar / rare fills). */
    accentSoft: 'linear-gradient(145deg, #3B82F6 0%, #2563EB 100%)',
  },
  radii: {
    card: 16,
    button: 8,
    pill: 24,
    chip: 20,
  },
  shadows: {
    card: '0 8px 16px rgba(0, 0, 0, 0.2)',
    cardHover: '0 16px 32px rgba(0, 0, 0, 0.3)',
    button: '0 4px 12px rgba(59, 130, 246, 0.3)',
    buttonHover: '0 6px 16px rgba(59, 130, 246, 0.5)',
    dialog: '0 12px 40px rgba(0, 0, 0, 0.45)',
  },
  dotGrid: {
    image: 'radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
    size: '30px 30px',
  },
} as const;

/** Soft accent fill helpers derived from accentRgb. */
export const accentAlpha = (a: number) =>
  `rgba(${tokens.colors.accentRgb}, ${a})`;

/** Page background used by the app shell (neutral wash + subtle dots). */
export const pageBackgroundSx = {
  backgroundColor: tokens.colors.bg,
  backgroundImage: `${tokens.dotGrid.image}, ${tokens.gradients.page}`,
  backgroundSize: `${tokens.dotGrid.size}, auto`,
} as const;

/**
 * Page / hero titles — solid type, not clipped gradient text.
 * Signature stays in the shell + accent bars, not rainbow headlines.
 */
export const brandTitleSx = {
  color: tokens.colors.text,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  textWrap: 'balance' as const,
};

/** Thin solid accent rule used on cards / panels. */
export const accentBarSx = {
  background: tokens.colors.accent,
} as const;
